import { APP_NAME, STRIKE_TYPES } from "../constants";
import { backendPublicRequest } from "../services/backendTransport";
import { Host } from "../models/hostModel";
import exceptionService from "../services/exceptionService";

// Function to calculate the zoom level based on longitudeDelta
export const calculateZoomLevel = (longitudeDelta: number) => {
    if (longitudeDelta < 0.01) {
        return 19; // High zoom level
    } else if (longitudeDelta < 0.05) {
        return 17;
    } else if (longitudeDelta < 0.1) {
        return 15;
    } else if (longitudeDelta < 0.118) { 
        return 14; 
    } else if (longitudeDelta < 0.3) { 
        return 13; 
    } else if (longitudeDelta < 0.5) {
        return 12;
    } else if (longitudeDelta < 0.8) { 
        return 11;
    }  else if (longitudeDelta < 1) {
        return 10;
    } else if (longitudeDelta < 5) {
        return 5;
    } else {
        return 1; // Low zoom level
    }
};

export const readFileAsArrayBuffer: any = (file: any) => { //Promise<ArrayBuffer>
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            resolve(reader.result as ArrayBuffer);
        };

        reader.onerror = () => {
            reject(new Error('Error reading file'));
        };

        reader.readAsArrayBuffer(file);
    });
};

export const formatDate: any = (dateString: string, hideTime? : boolean) => {
    const date = new Date(dateString);
    if(hideTime){
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            //hour: '2-digit',
            //minute: '2-digit',
            //hour12: true, // Change to false for 24-hour format
            });
    }else{
        return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true, // Change to false for 24-hour format
        });
    }
};

export const validatePhone = (phoneNumber: string) => {
    // Regular expression for validating phone numbers (basic 10 digits or with country code)
    //const phoneRegex = /^[+]?[0-9]{1,4}?[-.\s]?[0-9]{1,3}[-.\s]?[0-9]{1,4}[-.\s]?[0-9]{1,4}$/;
    //const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    //const phoneRegex = /^(\+?\s?\(?\d{1,4}\)?[\s\-]?)?(\(?\d{3}\)?[\s\-]?\d{3}[\s\-]?\d{4})$/;
    const phoneRegex = /^(\+?\s?\(?\d{1,4}\)?[\s\-]?)?(\(?\d{3}\)?[\s\-]?\d{3}[\s\-]?\d{4})$/;
    if (!phoneNumber) return false;
    // Remove any invisible, non-ASCII, or special whitespace characters
    const cleaned = phoneNumber.replace(/[^\x00-\x7F]|[\u200B-\u200D\uFEFF]/g, '').trim();
    //return result
    return phoneRegex.test(cleaned);
};  

export const formatPhoneNumber = (input: string | undefined) => {
    if(input){
        // Remove non-numeric characters
        let cleaned = input.replace(/\D/g, '');
        // Format the number based on length
        if (cleaned.length <= 3) {
            return `(${cleaned}`;
        } else if (cleaned.length <= 6) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
        } else {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
        }
    }
    return;
  };

export const stripPhoneNumber = (input: string) => {
    return input.replace(/\D/g, '');
};

export const parseAddress = (address: string) => {
    // Split the address into the street + city part and state + ZIP part
    const [street, city, stateAndZip, country] = address.split(",");
    // Clean the state and ZIP portion
    const [state, zip] = stateAndZip.trim().split(" ");
    // Return the components
    return { street, city, state, zip, country };
};


export const validatePasswordFormat = (password: string) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>?/`~]).{6,}$/;
    return passwordRegex.test(password);
};


export const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export const operationHours = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? "00" : "30";
    const period = hour < 12 ? "AM" : "PM";
    const formattedHour = hour % 12 === 0 ? 12 : hour % 12;    
    return { id: i, name: `${formattedHour}:${minute} ${period}` };
  });

export const convertTo12HourFormat = (time: string) => {
    try{
        const [hours, minutes] = time.split(':').map(Number);
        const period = hours >= 12 ? 'PM' : 'AM';
        const adjustedHour = hours % 12 || 12; // Convert 0 or 12 hours to 12 for AM/PM format
        return `${adjustedHour}:00 ${period}`;
        }catch(error){
            console.debug('Error in convertTo12HourFormat', error);
            exceptionService.logError(error, 'Error in ConvertTo12HourFormat');
    }
};

export const convertTo24HourFormat = (time: string) => {
    try{
        const [timePart, period] = time.split(' '); // Split the time and period (AM/PM)
        let [hours, minutes] = timePart.split(':').map(Number);
        if (period === 'PM' && hours !== 12) {
            hours += 12; // Convert PM times (except 12 PM) to 24-hour format
        } else if (period === 'AM' && hours === 12) {
            hours = 0; // Convert 12 AM to 0 hours in 24-hour format
        }
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }catch(error){
        console.debug('Error in convertTo24HourFormat', error);
        exceptionService.logError(error, 'Error in convertTo24HourFormat');
        return time;
    }

};

export const validateBusinessHours = (businessHours: Record<string, { open: string; close: string; closed: boolean }>): boolean => {
    return Object.entries(businessHours).every(([day, { open, close, closed }]) => {
        if (closed) return true; // If the day is marked as closed, skip validation

        const openTime = convertTo24HourForValidation(open);
        const closeTime = convertTo24HourForValidation(close);

        // Validate business hours:
        // 1. Standard same-day hours: openTime < closeTime (e.g., 9 AM - 5 PM)
        // 2. Open past midnight: openTime > closeTime, but closeTime is before 6 AM (e.g., 8 AM - 2 AM, 6 PM - 2 AM)
        // 3. Open for long operational hours but still closing the next morning (e.g., 6 AM - 2 AM)   
        return openTime < closeTime || (closeTime < 6 && openTime > closeTime);
    });
};
const convertTo24HourForValidation = (time: string): number => {
    const [hourMinute, period] = time.split(" ");
    let [hour, minute] = hourMinute.split(":").map(Number);

    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;

    return hour;
};

export const isHostOpenForUse = (host: Host): boolean => {
    /*
        order of checks - 
        1. closedForCleaning - closed for cleaning
        2. is24hours - business is 24 hours
        3. closedToday - closed for today per business hours
        4. currentlyOpen - checks based on time and business hours
    */
    if(host.closedForCleaning) return false;

    if(host.is24Hours) return true;

    if(host.closedToday) return false;

    return host.currentlyOpen;
};

export const isToday = (dateString: string) => {
    const inputDate = new Date(dateString); // Convert string to Date object
    const today = new Date(); // Get today's date
  
    // Compare year, month, and day
    return (
      inputDate.getFullYear() === today.getFullYear() &&
      inputDate.getMonth() === today.getMonth() &&
      inputDate.getDate() === today.getDate()
    );
};

export const strikeTypeText = (type: string) => {
    switch(type){
        case STRIKE_TYPES.POOR_CONDITIONS: 
            return 'Poor Conditions';
        case STRIKE_TYPES.SUSPEND_OR_BAN:
            return 'Suspension/Ban';
        case STRIKE_TYPES.NO_POST_USE_PICTURE:
            return 'No Picture Provided';
        default:
            return 'Account Strike';
    }
};

export const strikeTypeMessageText = (type: string, isBan: boolean) => {
    switch(type){
        case STRIKE_TYPES.POOR_CONDITIONS: 
            return 'The host or the user after you indicated you left the restroom in poor condition.';
        case STRIKE_TYPES.SUSPEND_OR_BAN:
            return isBan ? 'The host banned you from using this location. You can view more ban details on your profile.' : 'The host suspended you from using this location. You can view more suspension details on your profile.';            
        case STRIKE_TYPES.NO_POST_USE_PICTURE:
            return 'You did not provide an after use picture for this visit. All users are required to provide this upload to ensure the conditions of the restroom when a door lock code is required.';
        default:
            return 'Unkown account strike.';
    }
};

export const parseCurrentHours = (currentHours: any) => {
    if (typeof currentHours === 'string') {
        // If it's a string, parse it into an object
        return JSON.parse(currentHours);
    }
    if(!currentHours || currentHours == undefined){
        return daysOfWeek.reduce((acc, day) => ({
            ...acc,
            [day]: { open: '8:00 AM', close: '5:00 PM', closed: false }
        }), {})
    }
    return currentHours;
};

export const parseCurrentDoorLockHours = (currentHours: any) => {
    if (typeof currentHours === 'string') {
        // If it's a string, parse it into an object
        return JSON.parse(currentHours);
    }
    if(!currentHours || currentHours == undefined){
        return daysOfWeek.reduce((acc, day) => ({
            ...acc,
            [day]: { lock: '12:00 AM', unlock: '8:00 AM', doNotLock: false, lockAllDay: true }
        }), {})
    }
    return currentHours;
};

export const fetchRouteToHost = async (currentLocation: any, host: Host) => {
    if(!host || !currentLocation) return;
    const body = {
    origin: {
        location: { latLng: { latitude: currentLocation.latitude, longitude: currentLocation.longitude } },
    },
    destination: {
        location: { latLng: { latitude: host.latitude, longitude: host.longitude } },
    },
    travelMode: "DRIVE",
    };

    const { data, error } = await backendPublicRequest<string>('/google/route', body);
    if (!error && data) return decodePolyline(data);
    return null;
};

export const decodePolyline = (t: any) => {
    let points = [];
    let index = 0,
      lat = 0,
      lng = 0;

    while (index < t.length) {
      let b,
        shift = 0,
        result = 0;
      do {
        b = t.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      let dlat = result & 1 ? ~(result >> 1) : result >> 1;
      lat += dlat;

      shift = 0;
      result = 0;
      do {
        b = t.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      let dlng = result & 1 ? ~(result >> 1) : result >> 1;
      lng += dlng;

      points.push({ latitude: lat / 1e5, longitude: lng / 1e5 });
    }
    return points;
  };

export const formatPercent = (decimal: number, decimals = 0): string => {
  if (decimal === null || decimal === undefined) return '--';

  const percentValue = decimal * 100;

  return `${percentValue.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}%`;
};

    export const formatCurrency = (amount: number): string => {
    if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
    return `$${amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
    };

    export const formatNumber = (num: number, decimals: number = 0): string => {
    if (num === null || num === undefined || isNaN(num)) return (0).toFixed(decimals);
    return num.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
    };

    
